import React, { useCallback, useRef, useState } from 'react';
import { Project } from '../types';
import ProjectModal from '../components/projects/ProjectModal';
import { leadsService, projectsService } from '../services/supabase';
import { useToast } from '../context/ToastContext';
import { useQuota } from '../context/QuotaContext';
import { useI18n } from '../context/I18nContext';
import type { RequestCreateLeadArgs } from './useCreateLeadFromSearch';

export type RequestCreateProjectArgs = {
  prefillName: string;
  onCreated: (project: Project) => void;
};

type UseCreateProjectFromSearchOptions = {
  /** Forwarded to ProjectModal so lead search can create leads too. */
  onCreateLead?: (args: RequestCreateLeadArgs) => void;
};

/**
 * ProjectSearch "Créer un projet" → ProjectModal → select created project.
 */
export function useCreateProjectFromSearch(options: UseCreateProjectFromSearchOptions = {}) {
  const { t } = useI18n();
  const { showToast } = useToast();
  const { showQuotaLimit } = useQuota();
  const [isOpen, setIsOpen] = useState(false);
  const [defaultName, setDefaultName] = useState('');
  const onCreatedRef = useRef<((project: Project) => void) | null>(null);

  const requestCreateProject = useCallback(({ prefillName, onCreated }: RequestCreateProjectArgs) => {
    onCreatedRef.current = onCreated;
    setDefaultName(prefillName);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setDefaultName('');
    onCreatedRef.current = null;
  }, []);

  const finishCreate = useCallback(
    (project: Project, onCreated: ((project: Project) => void) | null) => {
      onCreatedRef.current = null;
      onCreated?.(project);
      showToast(t('projects.created', { name: project.name }) || t('common.save'), 'success');
      setIsOpen(false);
      setDefaultName('');
    },
    [showToast, t]
  );

  const handleSave = useCallback(
    async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
      // Keep onCreatedRef until success so a failed save can be retried (closeAfterSave).
      const onCreated = onCreatedRef.current;

      const created = await projectsService.create(projectData);

      if (created && (created as any).error === 'QUOTA_LIMIT' && (created as any).quotaInfo) {
        showQuotaLimit((created as any).quotaInfo, {
          onDismiss: () => {
            // X / upgrade — cancel the create-from-search attempt
            handleClose();
          },
          onSlotFreed: async () => {
            const retried = await projectsService.create(projectData) as any;
            if (retried && !retried.error && retried.id) {
              finishCreate(retried as Project, onCreated);
              return;
            }
            showToast(t('projects.createError') || t('common.error'), 'error');
          },
        });
        throw new Error('QUOTA_LIMIT');
      }

      if (!created || !(created as Project).id) {
        showToast(t('projects.createError') || t('common.error'), 'error');
        throw new Error('CREATE_FAILED');
      }

      finishCreate(created as Project, onCreated);
    },
    [finishCreate, handleClose, showQuotaLimit, showToast, t]
  );

  const projectCreateModal = (
    <ProjectModal
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSave}
      project={null}
      leads={[]}
      searchLeadsFn={(term) => leadsService.search(term, 10)}
      defaultName={defaultName}
      onCreateLead={options.onCreateLead}
      closeAfterSave
    />
  );

  return { requestCreateProject, projectCreateModal };
}
